import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Youtube, Facebook, Mail, MapPin, Users } from "lucide-react";
import { BRAND } from "@/data/content";
import { useContent } from "@/context/ContentContext";

export const Footer = () => {
  const { content } = useContent();
  const b = content.brand || {};
  const email = b.email || BRAND.email;
  const city = b.city || BRAND.city;
  const instagram = b.instagram || BRAND.socials.instagram;
  const youtube = b.youtube || BRAND.socials.youtube;
  const facebook = b.facebook || BRAND.socials.facebook;
  const community = b.whatsappCommunity || "";

  return (
    <footer
      data-testid="site-footer"
      className="relative mt-24 border-t border-gold/15 bg-midnight-deep"
    >
      <div className="wrap py-20 grid gap-12 md:grid-cols-4">
        <div className="md:col-span-2">
          <div className="font-display text-4xl md:text-5xl tracking-tight">
            <span className="text-ivory">Geomantic</span>{" "}
            <span className="text-gold-grad">Kalpataru</span>
          </div>
          <p className="mt-5 max-w-md text-ivory/65 leading-relaxed">
            A manifestation studio rooted in ancient geomancy and the Brahma Satya
            stream — created for the modern seeker who desires real, measurable
            transformation.
          </p>
          <div className="mt-8 flex gap-3 flex-wrap">
            {[
              { Icon: Instagram, href: instagram, label: "instagram" },
              { Icon: Youtube, href: youtube, label: "youtube" },
              { Icon: Facebook, href: facebook, label: "facebook" },
            ].map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                data-testid={`footer-social-${label}`}
                aria-label={label}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/30 text-gold hover:bg-gold hover:text-midnight transition"
              >
                <Icon className="h-4 w-4" strokeWidth={1.5} />
              </a>
            ))}
            {community && (
              <a
                href={community}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-social-whatsapp-community"
                aria-label="WhatsApp Community"
                title="Join WhatsApp Community"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-midnight transition text-[11px] uppercase tracking-[0.2em]"
              >
                <Users className="h-4 w-4" strokeWidth={1.5} /> Community
              </a>
            )}
          </div>
        </div>

        <div>
          <div className="eyebrow mb-4">Navigate</div>
          <ul className="space-y-2 text-ivory/70 text-sm">
            {[
              ["Home", "/"],
              ["Services", "/services"],
              ["Products", "/products"],
              ["Blog", "/blog"],
              ["Testimonials", "/testimonials"],
              ["Podcasts", "/podcasts"],
              ["Contact Us", "/contact"],
            ].map(([l, t]) => (
              <li key={l}>
                <Link to={t} className="hover:text-gold transition">
                  {l}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="eyebrow mb-4">Connect</div>
          <ul className="space-y-3 text-ivory/70 text-sm">
            <li className="flex items-start gap-2">
              <Mail className="h-4 w-4 mt-0.5 text-gold" strokeWidth={1.5} />
              <a href={`mailto:${email}`} className="hover:text-gold transition">
                {email}
              </a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="h-4 w-4 mt-0.5 text-gold" strokeWidth={1.5} />
              <span>{city}</span>
            </li>
          </ul>
          <Link
            to="/contact"
            className="btn-ghost mt-6"
            data-testid="footer-cta-contact"
          >
            Begin Your Journey
          </Link>
        </div>
      </div>

      <div className="border-t border-gold/10">
        <div className="wrap flex flex-col md:flex-row items-center justify-between gap-3 py-6 text-xs uppercase tracking-[0.25em] text-ivory/40">
          <div>© {new Date().getFullYear()} Geomantic Kalpataru. All rights reserved.</div>
          <div className="flex gap-6">
            <Link to="/contact" className="hover:text-gold">Privacy</Link>
            <Link to="/contact" className="hover:text-gold">Terms</Link>
            <Link to="/services" className="hover:text-gold">Declaration</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
